import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { PageHero } from "@/components/PageHero";
import Seo from "@/components/Seo";
import { HERO_ART } from "@/lib/heroes";
import { fetchGroups, createGroup, joinGroup, chantGroup } from "@/lib/api";
import { GROUP_CATS, catMeta } from "@/lib/groupCats";
import { Users, Plus, Loader2, X, CircleDot, ArrowRight } from "lucide-react";

const emptyForm = { name: "", category: "namasmarana", mantra_deva: "", mantra_en: "", deity: "", region: "", description: "", image_url: "", target: "" };

export default function Groups() {
  const { pick } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "moderator";
  const [groups, setGroups] = useState([]);
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState("");

  const load = () => { setLoading(true); fetchGroups(cat ? { category: cat } : {}).then(setGroups).finally(() => setLoading(false)); };
  useEffect(load, [cat]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (form.category === "namasmarana" && !form.mantra_deva.trim()) {
      toast(pick("मंत्र आवश्यक", "Mantra required"), { description: pick("नामस्मरण समूह के लिए मंत्र आवश्यक है।", "Namasmarana groups need a mantra.") });
      return;
    }
    setBusy("create");
    try {
      await createGroup({ ...form, target: parseInt(form.target || "0", 10) });
      toast(pick("समूह बनाया गया", "Group created"));
      setCreating(false); setForm(emptyForm); load();
    } catch (err) { toast(pick("नहीं बना", "Could not create"), { description: err?.response?.data?.detail || pick("पुनः प्रयास करें।", "Try again.") }); }
    finally { setBusy(""); }
  };

  const chant = async (g, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) return navigate("/login");
    setBusy(g.id);
    try {
      const updated = await chantGroup(g.id, 108);
      setGroups((gs) => gs.map((x) => x.id === g.id ? updated : x));
      toast("+108 japa", { description: g.name });
    } catch { toast(pick("दर्ज नहीं हुआ", "Could not record japa")); }
    finally { setBusy(""); }
  };
  const join = async (g, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) return navigate("/login");
    const updated = await joinGroup(g.id);
    setGroups((gs) => gs.map((x) => x.id === g.id ? updated : x));
    toast(pick("सम्मिलित हुए", "Joined"), { description: g.name });
  };

  return (
    <div data-testid="groups-page">
      <Seo path="/groups" title="Groups & Circles — Sangha on Dharmartha"
        description="Join deity circles, namasmarana groups, topic, region and purpose-based sanghas — chant together, discuss, and grow your sadhana with fellow seekers." />
      <PageHero art={HERO_ART.mantra} overline={pick("समुदाय · समूह", "Community · Groups")}
        deva="समूह एवं मंडली" en={pick("समूह एवं मंडली", "Groups & Circles")}
        tagline={pick("इष्ट देव, नामस्मरण, विषय, क्षेत्र एवं संकल्प आधारित मंडलियाँ — साथ जपें, संवाद करें एवं साधना बढ़ाएँ।", "Deity, namasmarana, topic, region and purpose circles — chant together, discuss, and deepen your sadhana.")} />

      <section className="mx-auto max-w-5xl px-5 py-12 md:px-8">
        {/* Category filters */}
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setCat("")} data-testid="group-cat-all"
            className={`border px-3 py-1.5 text-xs transition-colors ${!cat ? "border-saffron bg-saffron text-white" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"}`}>
            {pick("सभी", "All")}
          </button>
          {GROUP_CATS.map((c) => (
            <button key={c.key} onClick={() => setCat(c.key)} data-testid={`group-cat-${c.key}`}
              className={`border px-3 py-1.5 text-xs transition-colors ${cat === c.key ? "border-saffron bg-saffron text-white" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"}`}>
              {pick(c.deva, c.en)}
            </button>
          ))}
          {isAdmin && (
            <button onClick={() => setCreating((v) => !v)} data-testid="group-new-button"
              className="ml-auto inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--saffron-dark)]">
              <Plus className="h-4 w-4" /> {pick("नया समूह", "New group")}
            </button>
          )}
        </div>

        {/* Admin/mod composer */}
        {creating && isAdmin && (
          <form onSubmit={submit} data-testid="group-composer" className="mt-4 border border-line bg-surface p-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="group-name-input" placeholder={pick("समूह का नाम", "Group name")} className="border border-line bg-parchment px-3 py-2 text-ink focus:border-saffron focus:outline-none" />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="group-category-input" className="border border-line bg-parchment px-3 py-2 text-ink focus:border-saffron focus:outline-none">
                {GROUP_CATS.map((c) => <option key={c.key} value={c.key}>{pick(c.deva, c.en)}</option>)}
              </select>
            </div>
            {form.category === "namasmarana" && (
              <>
                <input value={form.mantra_deva} onChange={(e) => setForm({ ...form, mantra_deva: e.target.value })} data-testid="group-mantra-input" placeholder={pick("मंत्र (देवनागरी): ॐ नमः शिवाय", "Mantra (Devanagari): ॐ नमः शिवाय")} className="mt-3 w-full border border-line bg-parchment px-3 py-2 font-deva text-ink focus:border-saffron focus:outline-none" />
                <div className="mt-3 flex gap-3">
                  <input value={form.mantra_en} onChange={(e) => setForm({ ...form, mantra_en: e.target.value })} placeholder={pick("मंत्र (रोमन)", "Mantra (roman)")} className="flex-1 border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
                  <input value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} type="number" placeholder={pick("जप लक्ष्य", "Japa target")} className="w-40 border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
                </div>
              </>
            )}
            {form.category === "deity" && <input value={form.deity} onChange={(e) => setForm({ ...form, deity: e.target.value })} placeholder={pick("इष्ट देव (जैसे शिव)", "Deity (e.g. Shiva)")} className="mt-3 w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />}
            {form.category === "region" && <input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder={pick("क्षेत्र (जैसे काशी)", "Region (e.g. Kashi)")} className="mt-3 w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />}
            <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder={pick("चित्र URL (वैकल्पिक)", "Image URL (optional)")} className="mt-3 w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder={pick("समूह का विवरण", "What is this group about?")} className="mt-3 w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
            <div className="mt-3 flex gap-2">
              <button type="submit" disabled={busy === "create"} data-testid="group-submit" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">{busy === "create" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {pick("बनाएँ", "Create")}</button>
              <button type="button" onClick={() => setCreating(false)} className="border border-line px-4 py-2 text-sm text-ink-2"><X className="h-4 w-4" /></button>
            </div>
          </form>
        )}

        {/* Grid */}
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            <div className="col-span-full flex items-center gap-2 py-10 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> {pick("लोड हो रहा…", "Loading…")}</div>
          ) : groups.length === 0 ? (
            <div className="col-span-full py-14 text-center text-ink-3" data-testid="groups-empty">{pick("इस श्रेणी में अभी कोई समूह नहीं।", "No groups in this category yet.")}</div>
          ) : groups.map((g) => {
            const cm = catMeta(g.category);
            const isNama = g.category === "namasmarana";
            const pct = g.target ? Math.min(100, Math.round((g.total / g.target) * 100)) : null;
            return (
              <Link key={g.id} to={`/groups/${g.id}`} data-testid={`group-card-${g.id}`} className="group flex flex-col overflow-hidden border border-line bg-surface transition-shadow hover:shadow-md">
                <div className="relative h-28 w-full overflow-hidden" style={{ backgroundColor: `${cm.accent}14` }}>
                  {g.image_url ? <img src={g.image_url} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center font-deva text-3xl" style={{ color: cm.accent }}>{isNama ? g.mantra_deva?.[0] : (g.deity || g.name)?.[0]}</div>}
                  <span className="absolute left-2 top-2 rounded-sm px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white" style={{ backgroundColor: cm.accent }}>{pick(cm.deva, cm.en)}</span>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="font-serif-latin text-lg text-ink transition-colors group-hover:text-saffron">{g.name}</h3>
                  {isNama && g.mantra_deva && <p className="mt-0.5 font-deva text-lg text-saffron deva-lh">{g.mantra_deva}</p>}
                  {(g.deity || g.region) && <p className="mt-0.5 text-xs text-gold">{g.deity || g.region}</p>}
                  {g.description && <p className="mt-2 line-clamp-2 text-sm text-ink-2">{g.description}</p>}
                  <div className="mt-3 flex items-center justify-between text-xs text-ink-3">
                    <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {g.member_count} {pick("साधक", "members")}</span>
                    {isNama && <span data-testid={`group-total-${g.id}`}>{(g.total || 0).toLocaleString()} {pick("जप", "japa")}</span>}
                  </div>
                  {pct !== null && <div className="mt-2 h-1 w-full overflow-hidden bg-line"><div className="h-full bg-[var(--gold)]" style={{ width: `${pct}%` }} /></div>}
                  <div className="mt-4 flex items-center gap-2">
                    {isNama && <button onClick={(e) => chant(g, e)} disabled={busy === g.id} data-testid={`group-chant-${g.id}`} className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60">{busy === g.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CircleDot className="h-3.5 w-3.5" />} +108</button>}
                    {!g.joined && <button onClick={(e) => join(g, e)} data-testid={`group-join-${g.id}`} className="border border-line px-3 py-1.5 text-xs text-ink-2 hover:border-[var(--ink)] hover:text-ink">{pick("सम्मिलित हों", "Join")}</button>}
                    <span className="ml-auto inline-flex items-center gap-1 text-xs text-saffron opacity-0 transition-opacity group-hover:opacity-100">{pick("खोलें", "Open")} <ArrowRight className="h-3.5 w-3.5" /></span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
